import {useIsOverviewEnabled} from '../../hooks/use-feature-state'
import {useRespondToAliveUpdate} from '../../hooks/use-refetch-on-alive-update'
import {Button, IconButton} from '@primer/react'
import {useState} from 'react'
import styles from './RefreshButton.module.css'
import {SyncIcon} from '@primer/octicons-react'
import {SoftNavOnlyLink as RouterLink, useLocation} from '@github-ui/react-router'
import {
  useComparisonActions,
  usePullRequestBasePath,
  useUnifiedBatch,
  useUnifiedBatchActions,
} from '../../stores/layout-selectors'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQueryClient, type InfiniteData} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {useLayoutQuery, useLayoutQueryKey} from '../../page-data/loaders/use-layout-query'
import type {HeaderPageData} from '../../page-data/payloads/header'
import type {MainQueryResult} from '@github-ui/react-core/future/main-query'
import type {ThreadDataPage} from '../../page-data/loaders/use-threads'
import {threadsQueryKey} from '../../page-data/loaders/use-threads'
import {deriveRefreshedStatuses} from '../../helpers/unified-batch-revalidation'

export function RefreshButton({aliveChannel}: {aliveChannel: string}) {
  const [show, setShow] = useState(false)
  const {markComparisonOutdated, clearComparisonOutdated} = useComparisonActions()
  const {revalidateItems} = useUnifiedBatchActions()
  const {items} = useUnifiedBatch()
  const liveHeadSha = useLayoutQuery().data.pullRequest.headSha
  const headerQueryKey = useLayoutQueryKey()
  const basePath = usePullRequestBasePath()
  const updatedHeader = useIsOverviewEnabled()
  const queryClient = useQueryClient()

  const liveUpdateHandler = () => {
    markComparisonOutdated()
    setShow(true)
  }

  // we only need to display the refresh button when the base or head sha has changed (via git_updated)
  useRespondToAliveUpdate(aliveChannel, liveUpdateHandler, {git_updated: true})

  if (!show) {
    return null
  }

  /**
   * Refetches the caches per-item revalidation reads, reclassifies every batched item against that
   * fresh data, and advances the apply mutation's `expectedHeadOid` to the head it fetched at.
   *
   * Returns `false` when the refresh couldn't be completed, leaving item statuses, the baseline and
   * `isComparisonOutdated` untouched so the user can retry rather than committing against data we
   * never re-read.
   */
  const revalidateBatch = async (): Promise<boolean> => {
    try {
      // Pin the head first: this button appears the instant `git_updated` fires, but
      // `useHeaderLiveUpdates` refetches the header asynchronously, so the render-time
      // `liveHeadSha` can still be the pre-push value.
      await queryClient.invalidateQueries({queryKey: headerQueryKey}, {throwOnError: true})
      const revalidatedToSha =
        queryClient.getQueryData<MainQueryResult<HeaderPageData>>(headerQueryKey)?.payload.pullRequest.headSha ??
        liveHeadSha

      // Invalidate thread comments and historical positioning so active consumers refetch them.
      // Both caches use `staleTime: Infinity`, so a same-route soft-nav wouldn't refetch them on its own.
      // Interim: refetches every page thread. copilot-discovery-team#1014 tracks a `thread_ids` filter.
      await Promise.all([
        queryClient.invalidateQueries({queryKey: [PageData.threads]}, {throwOnError: true}),
        queryClient.invalidateQueries({queryKey: [PageData.threadPreviewPositioning]}, {throwOnError: true}),
      ])

      // Read the refetched cache directly rather than the store markers, which a passive effect
      // syncs later — this is provably the data fetched at `revalidatedToSha`. Scope to this PR's
      // exact key: the browser shares one QueryClient, so a bare `[PageData.threads]` prefix read
      // would pull in other visited PRs' caches and let their threads satisfy the guard below.
      const freshThreads = (
        queryClient.getQueryData<InfiniteData<ThreadDataPage>>(threadsQueryKey(basePath))?.pages ?? []
      ).flatMap(page => page.data)

      // The Rails loader falls back to `[]` on a database error, so an empty payload for a
      // non-empty batch is a data gap, not "every thread disappeared". Treat it as a failed refresh.
      if (items.length > 0 && freshThreads.length === 0) return false

      revalidateItems(deriveRefreshedStatuses(items, freshThreads), revalidatedToSha)
      return true
    } catch {
      return false
    }
  }

  const handleRefresh = async () => {
    // Gated so no request fires for users without the panel.
    if (isFeatureEnabled('unified_batch_pr_comments') && !(await revalidateBatch())) return

    // Cleared only once the refresh succeeded, so a failure keeps progressive diff loads sending
    // `outdated=1` and leaves the Refresh control up for a retry.
    clearComparisonOutdated()
    if (updatedHeader) {
      setShow(false)
    }
  }

  return <DataRouterRefreshButton onRefresh={handleRefresh} />
}

function DataRouterRefreshButton({onRefresh}: {onRefresh: () => void | Promise<void>}) {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const updatedHeader = useIsOverviewEnabled()

  const onClick = () => {
    setIsLoading(true)
    // Reset loading regardless of outcome so a failed refresh doesn't leave the button spinning.
    void Promise.resolve(onRefresh()).finally(() => setIsLoading(false))
  }

  const sharedProps = {
    as: RouterLink,
    to: location.pathname,
    variant: 'invisible' as const,
    size: 'small' as const,
    onClick,
    loading: isLoading,
    preventScrollReset: true,
    'data-refresh-button-visible': true,
  }

  return updatedHeader ? (
    <>
      <IconButton
        {...sharedProps}
        icon={SyncIcon}
        aria-label="Refresh"
        className={`${styles.refresh} d-flex d-md-none`}
      />
      <Button
        {...sharedProps}
        leadingVisual={SyncIcon}
        aria-label="Refresh"
        className={`${styles.refresh} d-none d-md-flex`}
      >
        Refresh
      </Button>
    </>
  ) : (
    <Button {...sharedProps} leadingVisual={SyncIcon} aria-label="Refresh" className={`${styles.refresh}`}>
      Refresh
    </Button>
  )
}
